// app.js — entry point.
//
// Boots the platform services, mounts the app shell, registers the service
// worker. This is the file index.html loads as a module.

import { setLanguage } from "./core/i18n.js";
import { bootstrap } from "./platform-services.js";
import { defineComponent } from "./core/component.js";
import * as entitlementService from "./engines/entitlement-service.js";
// Side-effect + API: attaches the cross-domain token listener at boot (as early
// as possible, so we catch the parent's on-load `sg-auth` post) and exposes the
// entitlement announce for the studio page. See auth/session.js + Brief 005b.
import * as session from "./auth/session.js";

import { RMAppShell } from "./screens/rm-app-shell.js";
import { RMHome } from "./screens/rm-home.js";
import { RMIntro } from "./screens/rm-intro.js";
import { RMList } from "./screens/rm-list.js";
import { RMPlayer } from "./screens/rm-player.js";
import { RMHowTo } from "./screens/rm-how-to.js";

import { RMListItem } from "./ui/rm-list-item.js";
import { RMLyricsDisplay } from "./ui/rm-lyrics-display.js";
import { RMProgressBubble } from "./ui/rm-progress-bubble.js";
import { RMAudioBar } from "./ui/rm-audio-bar.js";
import { RMHtmlPane } from "./ui/rm-html-pane.js";
import { RMUpgradeCard } from "./ui/rm-upgrade-card.js";

// True on local dev hosts (mirrors sw.js / entitlement-source.js).
function isLocalHost() {
  const h = location.hostname;
  return (
    h === "localhost" ||
    h.endsWith(".localhost") ||
    h === "127.0.0.1" ||
    h === "::1" ||
    h === "[::1]"
  );
}

// Define every custom element used in the app. Order doesn't matter; tags
// resolve lazily.
defineComponent("rm-app-shell", RMAppShell);
defineComponent("rm-home", RMHome);
defineComponent("rm-intro", RMIntro);
defineComponent("rm-list", RMList);
defineComponent("rm-player", RMPlayer);
defineComponent("rm-how-to", RMHowTo);
defineComponent("rm-list-item", RMListItem);
defineComponent("rm-lyrics-display", RMLyricsDisplay);
defineComponent("rm-progress-bubble", RMProgressBubble);
defineComponent("rm-audio-bar", RMAudioBar);
defineComponent("rm-html-pane", RMHtmlPane);
defineComponent("rm-upgrade-card", RMUpgradeCard);

(async () => {
  // Kick off entitlement resolution as early as possible. The gate defaults to
  // the free set while this is pending, so the UI never flashes a lockout.
  const entitlementReady = entitlementService.init();

  const lang = (navigator.language || "en").startsWith("zh") ? "zh-Hans" : "en";
  await setLanguage(lang);
  await bootstrap();
  await entitlementReady; // resolved before the first interactive route / rm:ready

  // Tell the parent studio page whether this user is paid, so it can reveal the
  // phrase-book downloads / hide the upsell. No-op when running standalone.
  session.announceEntitlement(entitlementService.status() === "entitled");

  // Localhost-only entitlement state switcher. The guard references the
  // build-time `__IS_LOCAL_DEV__` define DIRECTLY in the `if` (not via an
  // intermediate const) so esbuild folds it to `if (false)` in production and
  // dead-code-eliminates this dynamic import — keeping the dev toggle out of
  // the prod bundle (Brief 005a criterion #6). In unbundled dev the define is
  // absent, so the `typeof` guard falls back to a hostname check.
  if (typeof __IS_LOCAL_DEV__ !== "undefined" ? __IS_LOCAL_DEV__ : isLocalHost()) {
    import("./ui/rm-dev-ent-toggle.js")
      .then((m) => m.mountDevEntToggle())
      .catch((err) => console.warn("[app] dev ent toggle failed", err));
  }

  // Register the service worker last — the app must work without it.
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.register("./sw.js").catch((err) => {
      console.warn("[app] sw registration failed", err);
    });
  }

  document.dispatchEvent(new Event("rm:ready"));
})();
